import audioData from '../shared/weapon-audio.json';
import propAudio from '../shared/prop-audio.json';
import feedback from '../shared/game-feedback-audio.json';
import battleMusic from '../shared/battle-music.json';
import type { ShotResult } from '../shared/types';
import { volleyLaunches } from './character-animation';
import { attackTiming, ORIGINAL_ACTION_TICK_MS } from '../shared/attack-timing';
interface ClipPair { shoot: string; impact: string }
interface SoundInfo { url: string; startOffset: number; duration: number }
const sounds = audioData as unknown as { weapons: Record<string, { normal: ClipPair; special: ClipPair }>; actions: Record<string, ClipPair>; ui: Record<string, string>; sounds: Record<string, SoundInfo> };
const metadata = new Map<string, SoundInfo>([...Object.values(sounds.sounds), ...Object.values(propAudio.sounds), ...Object.values(feedback.ui), ...Object.values(feedback.battle)].map(info => [info.url, info]));
const musicMaps: Record<string, SoundInfo> = battleMusic.maps;
const MAX_AUDIO_DOWNLOADS = 4;
const MAX_AUDIO_DECODES = 4;
const AUDIO_LOAD_TIMEOUT_MS = 60_000;
interface LoadQueue { active: number; queued: Array<() => void>; limit: number }

/** ShootBombAction plays one voice per salvo; SimpleBomb plays each impact. */
export class GameAudio {
  private context: AudioContext | null = null;
  private buffers = new Map<string, Promise<AudioBuffer>>();
  private bytes = new Map<string, Promise<ArrayBuffer>>();
  private downloaded = new Set<string>();
  private voices = new Set<AudioBufferSourceNode>();
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private shots = new Set<string>();
  private generation = 0;
  private channels = new Set<string>();
  private channelGenerations = new Map<string, number>();
  private voiceChannels = new Map<AudioBufferSourceNode, string>();
  private preview: { playerId: string; weaponId: string; kind: ShotResult['kind']; at: number; emitted: boolean; pending: boolean;
    confirmedShotId: string | null; confirmedEmitAt?: number; recoveryScheduled: boolean; timer: ReturnType<typeof setTimeout> | null } | null = null;
  private events: { path: string; at: number }[] = [];
  private requestedMusicMap: string | null = null;
  private musicGeneration = 0;
  private musicPending: object | null = null;
  private musicVoice: { mapId: string; source: AudioBufferSourceNode; gain: GainNode; offset: number } | null = null;
  private musicEpoch: number | null = null;
  private musicError: string | null = null;
  private decoded = new Set<string>();
  private preloadPaths = [...new Set([...metadata.keys(), ...Object.values(musicMaps).map(info => info.url)])];
  private preloadLoading: Promise<boolean> | null = null;
  private failedBuffers = new Set<string>();
  private transfers: LoadQueue = { active: 0, queued: [], limit: MAX_AUDIO_DOWNLOADS };
  private decodes: LoadQueue = { active: 0, queued: [], limit: MAX_AUDIO_DECODES };
  onLoadProgress?: () => void;
  constructor(private muted: () => boolean, private delivery?: { has(path: string): boolean; file(path: string): Promise<Blob | null> }) {}
  statusAll() {
    const pending = this.preloadPaths.filter(path => !this.decoded.has(path));
    return { ready: pending.length === 0, total: this.preloadPaths.length, loaded: this.preloadPaths.length - pending.length, pending,
      failed: pending.filter(path => this.failedBuffers.has(path)), downloaded: this.downloaded.size,
      activeLoads: this.transfers.active + this.decodes.active, queuedLoads: this.transfers.queued.length + this.decodes.queued.length,
      activeDownloads: this.transfers.active, queuedDownloads: this.transfers.queued.length,
      activeDecodes: this.decodes.active, queuedDecodes: this.decodes.queued.length };
  }
  /** Decoding works while suspended; playback still waits for a user gesture. */
  preloadAll(): Promise<boolean> {
    if (this.statusAll().ready) return Promise.resolve(true);
    if (this.preloadLoading) return this.preloadLoading;
    this.context ??= new AudioContext();
    const pending = this.preloadPaths.filter(path => !this.decoded.has(path));
    // Queue the whole inventory once. Download slots are released as soon as
    // bytes arrive, so a long music decode cannot stall the next effect fetch.
    const loading = Promise.allSettled(pending.map(path => this.buffer(path)))
      .then(results => results.every(result => result.status === 'fulfilled') && this.statusAll().ready)
      .finally(() => { this.preloadLoading = null; this.onLoadProgress?.(); });
    this.preloadLoading = loading;
    return loading;
  }
  activate() {
    this.context ??= new AudioContext();
    void this.context.resume().then(() => this.syncBattleMusic()).catch(() => {});
    for (const info of metadata.values()) void this.buffer(info.url).catch(() => {});
    void this.syncBattleMusic();
  }
  /** GameViewBase selects MapInfo.BackMusic; repeated room packets reuse it. */
  setBattleMusic(mapId: string | null) {
    const next = mapId && musicMaps[mapId] ? mapId : null;
    if (next !== this.requestedMusicMap) {
      this.stopMusicVoice();
      this.requestedMusicMap = next;
      this.musicEpoch = null;
    }
    if (this.muted()) this.stopMusicVoice();
    else void this.syncBattleMusic();
  }
  private stopMusicVoice() {
    this.musicGeneration++;
    this.musicPending = null;
    this.musicError = null;
    const voice = this.musicVoice;
    this.musicVoice = null;
    if (voice) {
      try { voice.source.stop(); } catch { /* Voice may have ended. */ }
      voice.source.disconnect(); voice.gain.disconnect();
    }
  }
  private async syncBattleMusic() {
    const context = this.context, mapId = this.requestedMusicMap;
    if (!context || context.state !== 'running' || !mapId || this.muted() || this.musicVoice || this.musicPending) return;
    const pending = this.musicPending = {}, generation = this.musicGeneration;
    let createdVoice: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
    try {
      const buffer = await this.buffer(musicMaps[mapId].url);
      if (pending !== this.musicPending || generation !== this.musicGeneration || mapId !== this.requestedMusicMap || context.state !== 'running' || this.muted()) return;
      if (!Number.isFinite(buffer.duration) || buffer.duration <= 0) throw new Error('Empty battle music');
      const source = context.createBufferSource(), gain = context.createGain();
      createdVoice = { source, gain };
      source.buffer = buffer;
      // The original unchanged [id,id] playlist restarts list[0] on every
      // NetStream.Play.Stop. One looping voice has the same playback order.
      source.loop = battleMusic.rules.repeat;
      source.loopStart = 0; source.loopEnd = buffer.duration;
      gain.gain.value = battleMusic.rules.defaultGain;
      source.connect(gain).connect(context.destination);
      // Source mute only zeroes the volume while its stream keeps advancing.
      // Our mute/reset stops the voice, then resumes at that elapsed position.
      const epoch = this.musicEpoch ?? context.currentTime;
      const offset = Math.max(0, context.currentTime - epoch) % buffer.duration;
      const voice = { mapId, source, gain, offset };
      source.onended = () => {
        source.disconnect(); gain.disconnect();
        if (this.musicVoice === voice) this.musicVoice = null;
      };
      source.start(0, offset);
      this.musicEpoch = epoch;
      this.musicVoice = voice;
      this.musicError = null;
    } catch (error) {
      createdVoice?.source.disconnect(); createdVoice?.gain.disconnect();
      if (pending === this.musicPending && generation === this.musicGeneration) this.musicError = error instanceof Error ? error.message : String(error);
    } finally {
      if (pending === this.musicPending) this.musicPending = null;
    }
  }
  private buffer(path: string) {
    let promise = this.buffers.get(path);
    if (!promise) {
      const context = this.context!;
      this.failedBuffers.delete(path);
      promise = this.audioBytes(path).then(bytes => this.scheduleLoad(this.decodes, () => new Promise<AudioBuffer>((resolve, reject) => {
        let finished = false;
        const fail = (error: unknown, invalidBytes = false) => {
          if (finished) return;
          finished = true; clearTimeout(timeout);
          // A decoder rejection may indicate corrupt bytes. A timeout only
          // retires this decode; retain its completed transfer for retry.
          if (invalidBytes) { this.bytes.delete(path); this.downloaded.delete(path); }
          reject(error);
        };
        const timeout = setTimeout(() => fail(new Error('Audio decode timed out')), AUDIO_LOAD_TIMEOUT_MS);
        // decodeAudioData can detach its input; retain the original until ready.
        void Promise.resolve().then(() => context.decodeAudioData(bytes.slice(0))).then(decoded => {
          if (finished) return;
          if (!Number.isFinite(decoded.duration) || decoded.duration <= 0) { fail(new Error('Empty audio buffer'), true); return; }
          finished = true; clearTimeout(timeout); this.decoded.add(path); this.bytes.delete(path); resolve(decoded);
        }).catch(error => fail(error, true));
      }))).catch((error) => {
        this.failedBuffers.add(path);
        if (this.buffers.get(path) === promise) this.buffers.delete(path);
        throw error;
      }).finally(() => this.onLoadProgress?.());
      this.buffers.set(path, promise);
    }
    return promise;
  }
  private audioBytes(path: string) {
    let promise = this.bytes.get(path);
    if (!promise) {
      const packed = this.delivery?.has(path) ? this.delivery.file(path) : null;
      promise = (packed ? packed.then(async file => {
        if (!file) throw Error('Missing packed audio');
        const bytes = await file.arrayBuffer(); this.downloaded.add(path); return bytes;
      }) : this.scheduleLoad(this.transfers, () => new Promise<ArrayBuffer>((resolve, reject) => {
        const controller = new AbortController();
        let finished = false;
        const fail = (error: unknown) => {
          if (finished) return;
          finished = true; clearTimeout(timeout); controller.abort(); reject(error);
        };
        const timeout = setTimeout(() => fail(new Error('Audio download timed out')), AUDIO_LOAD_TIMEOUT_MS);
        void Promise.resolve().then(() => fetch(path, { signal: controller.signal })).then(async (response) => {
          if (!response.ok) throw new Error(`Audio ${response.status}`);
          const bytes = await response.arrayBuffer();
          if (finished) return;
          finished = true; clearTimeout(timeout); this.downloaded.add(path); resolve(bytes);
        }).catch(fail);
      }))).catch((error) => {
        if (this.bytes.get(path) === promise) this.bytes.delete(path);
        throw error;
      }).finally(() => this.onLoadProgress?.());
      this.bytes.set(path, promise);
    }
    return promise;
  }
  /** Entry preload and playback callers share both bounded pipeline stages. */
  private scheduleLoad<T>(queue: LoadQueue, load: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.queued.push(() => {
        queue.active++;
        void Promise.resolve().then(load).then(resolve, reject).finally(() => {
          queue.active--; this.drainLoads(queue);
        });
      });
      this.drainLoads(queue);
    });
  }
  private drainLoads(queue: LoadQueue): void {
    while (queue.active < queue.limit && queue.queued.length) queue.queued.shift()!();
  }
  private async play(path: string | undefined, volume = .45, channel?: string, onStarted?: () => void, maxAgeMs = 250) {
    if (!path || this.muted() || (channel && this.channels.has(channel))) return false;
    const requestedAt = performance.now();
    if (channel) this.channels.add(channel);
    const channelGeneration = channel ? this.channelGenerations.get(channel) ?? 0 : 0;
    if (!this.context) this.activate();
    else if (this.context.state === 'suspended') void this.context.resume().then(() => this.syncBattleMusic()).catch(() => {});
    const context = this.context!, generation = this.generation;
    try {
      const buffer = await this.buffer(path);
      // A shared cold buffer can resolve many transient requests together.
      // Expire those events instead of replaying an old input burst now.
      if (performance.now() - requestedAt > maxAgeMs || context.state !== 'running' || this.muted() || generation !== this.generation || (channel && channelGeneration !== (this.channelGenerations.get(channel) ?? 0))) {
        if (channel && generation === this.generation && channelGeneration === (this.channelGenerations.get(channel) ?? 0)) this.channels.delete(channel);
        return false;
      }
      const source = context.createBufferSource(), gain = context.createGain();
      source.buffer = buffer;
      gain.gain.value = volume;
      source.connect(gain).connect(context.destination);
      this.voices.add(source);
      if (channel) this.voiceChannels.set(source, channel);
      source.onended = () => { source.disconnect(); gain.disconnect(); this.voices.delete(source); this.voiceChannels.delete(source); if (channel && generation === this.generation && channelGeneration === (this.channelGenerations.get(channel) ?? 0)) this.channels.delete(channel); };
      // SWF DefineSound SeekSamples trims encoder padding, not event timing.
      const info = metadata.get(path), offset = info?.startOffset ?? 0;
      source.start(0, offset, Math.max(.001, Math.min(info?.duration ?? buffer.duration, buffer.duration - offset)));
      this.events.push({ path, at: performance.now() });
      if (this.events.length > 80) this.events.shift();
      onStarted?.();
      return true;
    } catch { if (channel && generation === this.generation && channelGeneration === (this.channelGenerations.get(channel) ?? 0)) this.channels.delete(channel); }
    return false;
  }
  private at(delay: number, path: string, volume?: number, onStarted?: () => void, channel?: string, onFinished?: (started: boolean) => void) {
    if (delay < -250) return null;
    const timer = setTimeout(() => { this.timers.delete(timer); void this.play(path, volume, channel, onStarted).then(started => onFinished?.(started)); }, Math.max(0, delay));
    this.timers.add(timer);
    return timer;
  }
  shot(shot: ShotResult, serverNow: number) {
    if (this.shots.has(shot.id)) return;
    this.shots.add(shot.id);
    const pair = sounds.actions[shot.kind] ?? sounds.weapons[shot.weaponId]?.[shot.special ? 'special' : 'normal'];
    if (!pair) return;
    const elapsed = serverNow - shot.createdAt;
    const launches = volleyLaunches(shot);
    const preview = this.preview;
    const matchingPreview = preview?.playerId === shot.playerId && preview.weaponId === shot.weaponId && preview.kind === shot.kind
      && (!preview.confirmedShotId || preview.confirmedShotId === shot.id) && performance.now() - preview.at < 3000;
    const localFirstVoice = matchingPreview && (preview.pending || preview.emitted);
    // Accept the already-scheduled release event, including a decode in
    // progress. Cancel/recreate on ACK would add another network delay to the
    // windup, or race the pending decode into a second voice.
    if (matchingPreview && localFirstVoice) {
      preview.confirmedShotId = shot.id;
      preview.confirmedEmitAt = performance.now() + (launches[0] ?? 0) - elapsed;
    } else if (matchingPreview) this.cancelPreview(shot.playerId);
    for (const [index, launch] of launches.entries()) if (index !== 0 || !localFirstVoice) this.at(launch - elapsed, pair.shoot, .4);
    for (const impact of shot.impacts) this.at(impact.at - elapsed, pair.impact, .45);
    // Death belongs to the presented alive -> dead transition (also covers
    // falling out). Do not schedule a second voice from the damage packet.
  }
  previewShot(playerId: string, weaponId: string, kind: ShotResult['kind']) {
    this.cancelPreview();
    const preview = this.preview = { playerId, weaponId, kind, at: performance.now(), emitted: false, pending: false,
      confirmedShotId: null, recoveryScheduled: false, timer: null } as NonNullable<GameAudio['preview']>;
    const pair = sounds.actions[kind] ?? sounds.weapons[weaponId]?.[kind === 'special' ? 'special' : 'normal'];
    if (!pair) return;
    const started = () => { preview.emitted = true; };
    const finished = (didStart: boolean) => {
      preview.pending = false; preview.timer = null;
      if (didStart || this.preview !== preview || preview.confirmedEmitAt === undefined || preview.recoveryScheduled) return;
      // Preload normally makes this immediate. If a cold local voice expires,
      // one still-fresh authority event may recover it; never retry a stale
      // event or reschedule an already-started voice.
      preview.recoveryScheduled = true;
      preview.timer = this.at(preview.confirmedEmitAt - performance.now(), pair.shoot, .4, started, 'previewShot', () => { preview.pending = false; preview.timer = null; });
      preview.pending = preview.timer !== null;
    };
    preview.pending = true;
    preview.timer = this.at(attackTiming(weaponId, kind).windupMs, pair.shoot, .4, started, 'previewShot', finished);
  }
  cancelPreview(playerId?: string, stopStarted = true) {
    if (!this.preview || (playerId && this.preview.playerId !== playerId)) return;
    if (this.preview.timer !== null) { clearTimeout(this.preview.timer); this.timers.delete(this.preview.timer); }
    this.stopChannel('previewShot', stopStarted);
    this.preview = null;
  }
  private stopChannel(channel: string, stopStarted = true) {
    this.channelGenerations.set(channel, (this.channelGenerations.get(channel) ?? 0) + 1);
    this.channels.delete(channel);
    for (const [voice, name] of this.voiceChannels) if (name === channel && stopStarted) {
      try { voice.stop(); } catch { /* Voice may have ended. */ }
      this.voiceChannels.delete(voice); this.voices.delete(voice);
    }
  }
  charge() { void this.play(feedback.battle.charge.url, .25, 'charge'); }
  // Angle input belongs to one original 25fps tick, not a decode backlog.
  aim() { void this.play(feedback.battle.aim.url, .25, undefined, undefined, ORIGINAL_ACTION_TICK_MS); }
  cancelCharge() { this.stopChannel('charge'); }
  releaseCharge() { this.cancelCharge(); void this.play(feedback.battle.release.url, .35); }
  walk() { void this.play(feedback.battle.walk.url, .3, 'walk'); }
  death() { void this.play(feedback.battle.death.url, .4); }
  result(won: boolean, draw: boolean) {
    this.setBattleMusic(null);
    void this.play(feedback.ui.gameOver.url, .45);
    if (!draw) this.at(650, (won ? feedback.ui.win : feedback.ui.lose).url, .4);
  }
  get debug() { return { context: this.context?.state ?? 'inactive', loaded: this.buffers.size, voices: this.voices.size, events: [...this.events],
    music: { requestedMap: this.requestedMusicMap, playingMap: this.musicVoice?.mapId ?? null, loading: this.musicPending !== null,
      url: this.requestedMusicMap ? musicMaps[this.requestedMusicMap].url : null, loop: battleMusic.rules.repeat,
      gain: battleMusic.rules.defaultGain, offset: this.musicVoice?.offset ?? null, error: this.musicError } }; }
  propActivate() { void this.play(propAudio.ui.activate.url, .35); }
  propUse(pow = false) { void this.play((pow ? propAudio.battle.pow : propAudio.battle.use).url, .45); }
  turn(self: boolean) { void this.play(sounds.ui[self ? 'selfTurn' : 'otherTurn'], .4); }
  reset() {
    // Keep the requested map for unmute/user-gesture retry. Room disposal
    // explicitly calls setBattleMusic(null) before resetting transient effects.
    this.stopMusicVoice();
    this.generation++;
    for (const timer of this.timers) clearTimeout(timer);
    for (const voice of this.voices) { try { voice.stop(); } catch { /* Already stopped. */ } }
    this.timers.clear(); this.voices.clear(); this.voiceChannels.clear(); this.shots.clear(); this.channels.clear(); this.channelGenerations.clear(); this.preview = null;
  }
}
